import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ValidateIcon,
  Refresh as RefreshIcon,
  AccountBalance as LoanIcon,
  PictureAsPdf as PdfIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { pretsAPI, employeesAPI } from '../../../services/api';
import {
  Pret,
  PretStats,
  STATUT_PRET_OPTIONS,
  getStatutPretLabel,
  getStatutPretColor,
} from '../../../types/pret';
import { EmployeeDropdownItem } from '../../../types/employee';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import PretFormDialog from './PretFormDialog';
import PretValidationDialog from './PretValidationDialog';
import PretHistoriqueDialog from './PretHistoriqueDialog';
import ColumnFilter from '../../../components/ColumnFilter';
import { useColumnFilters } from '../../../hooks/useColumnFilters';

const HR_COLOR = '#00897B';

const PRET_FILTER_SCHEMA = {
  ref_pret:     { type: 'text',         param: 'ref_pret' },
  date_demande: { type: 'dateRange',    paramFrom: 'date_demande_from', paramTo: 'date_demande_to' },
  employee_id:  { type: 'autocomplete', param: 'employee_id', transform: (v: string) => Number(v) },
  montant:      { type: 'numberRange',  paramMin: 'montant_min',     paramMax: 'montant_max' },
  mensualite:   { type: 'numberRange',  paramMin: 'mensualite_min',  paramMax: 'mensualite_max' },
  duree:        { type: 'numberRange',  paramMin: 'duree_min',       paramMax: 'duree_max' },
  statut:       { type: 'select',       param: 'statut' },
} as const;

const PretsManagement: React.FC = () => {
  const [prets, setPrets] = useState<Pret[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState<PretStats | null>(null);

  // Dialog states
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [selectedPret, setSelectedPret] = useState<Pret | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pretToDelete, setPretToDelete] = useState<Pret | null>(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [pretToValidate, setPretToValidate] = useState<Pret | null>(null);
  const [historiqueDialogOpen, setHistoriqueDialogOpen] = useState(false);
  const [pretForHistorique, setPretForHistorique] = useState<Pret | null>(null);

  // Employees for dropdown
  const [employees, setEmployees] = useState<EmployeeDropdownItem[]>([]);

  const { showSuccess, showError } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    PRET_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  const fetchPrets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await pretsAPI.getAll({
        page: page + 1,
        size: rowsPerPage,
        search: search || undefined,
        ...toQueryParams(),
      });
      setPrets(response.items);
      setTotalCount(response.total);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, filters, showError]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await pretsAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const data = await employeesAPI.getAllForDropdown();
      setEmployees(data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  useEffect(() => {
    fetchPrets();
    fetchStats();
  }, [fetchPrets, fetchStats]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddPret = () => {
    setSelectedPret(null);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleEditPret = (pret: Pret) => {
    setSelectedPret(pret);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (pret: Pret) => {
    setPretToDelete(pret);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!pretToDelete) return;
    try {
      await pretsAPI.delete(pretToDelete.id);
      showSuccess('Demande de prêt supprimée avec succès');
      fetchPrets();
      fetchStats();
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setDeleteDialogOpen(false);
      setPretToDelete(null);
    }
  };

  const handleValidateClick = (pret: Pret) => {
    setPretToValidate(pret);
    setValidationDialogOpen(true);
  };

  const handleProgressClick = (pret: Pret) => {
    setPretForHistorique(pret);
    setHistoriqueDialogOpen(true);
  };

  const handleSuccess = () => {
    fetchPrets();
    fetchStats();
  };

  const handleGeneratePDF = (pret: Pret, language: 'fr' | 'ar') => {
    openPreview({
      getBlob: () =>
        language === 'ar'
          ? pretsAPI.getArabicPDFBlob(pret.id)
          : pretsAPI.getFrenchPDFBlob(pret.id),
      fileName: `pret_${pret.id}_${language}.pdf`,
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('fr-MA', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) + ' DH';
  };

  return (
    <Box sx={{ p: 0 }}>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: HR_COLOR, color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LoanIcon />
                <Typography variant="h6">{stats?.total_demandes || 0}</Typography>
              </Box>
              <Typography variant="body2">Total Demandes</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#FFA726', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats?.total_en_attente || 0}</Typography>
              <Typography variant="body2">En Attente</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#66BB6A', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats?.total_validees || 0}</Typography>
              <Typography variant="body2">Validés</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#EF5350', color: 'white' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="h6">{stats?.total_refusees || 0}</Typography>
              <Typography variant="body2">Refusés</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Financial Summary */}
      {stats && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Montant Total Prêté</Typography>
              <Typography variant="h6" sx={{ color: HR_COLOR }}>
                {formatMoney(stats.total_montant_prete)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Montant Remboursé</Typography>
              <Typography variant="h6" sx={{ color: '#66BB6A' }}>
                {formatMoney(stats.total_montant_rembourse)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">Montant Restant</Typography>
              <Typography variant="h6" sx={{ color: '#EF5350' }}>
                {formatMoney(stats.total_montant_restant)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Rechercher un employé ou réf..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />

          {hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              Effacer les filtres
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="Actualiser">
            <IconButton onClick={() => { fetchPrets(); fetchStats(); }} sx={{ color: HR_COLOR }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddPret}
            sx={{ bgcolor: HR_COLOR, '&:hover': { bgcolor: '#00695C' } }}
          >
            Nouvelle Demande
          </Button>
        </Box>

        {/* Table */}
        <TableContainer sx={getModuleTableTheme('hr')}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Réf.
                    <ColumnFilter type="text" value={filters.ref_pret} onChange={(v) => update('ref_pret', v)} label="référence" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date Demande
                    <ColumnFilter type="dateRange" value={filters.date_demande} onChange={(v) => update('date_demande', v)} label="date demande" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Employé
                    <ColumnFilter
                      type="autocomplete"
                      value={filters.employee_id}
                      onChange={(v) => update('employee_id', v)}
                      label="employé"
                      color={HR_COLOR}
                      options={employees.map(e => ({ value: e.id.toString(), label: `${e.matricule || ''} ${e.nom_complet}`.trim() }))}
                    />
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    Montant
                    <ColumnFilter type="numberRange" value={filters.montant} onChange={(v) => update('montant', v)} label="montant" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    Mensualité
                    <ColumnFilter type="numberRange" value={filters.mensualite} onChange={(v) => update('mensualite', v)} label="mensualité" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Durée
                    <ColumnFilter type="numberRange" value={filters.duree} onChange={(v) => update('duree', v)} label="durée (mois)" color={HR_COLOR} />
                  </Box>
                </TableCell>
                <TableCell>Remboursement</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Statut
                    <ColumnFilter
                      type="select"
                      value={filters.statut}
                      onChange={(v) => update('statut', v)}
                      label="statut"
                      color={HR_COLOR}
                      options={STATUT_PRET_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
                    />
                  </Box>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">Chargement...</TableCell>
                </TableRow>
              ) : prets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">Aucune demande trouvée</TableCell>
                </TableRow>
              ) : (
                prets.map((pret) => (
                  <TableRow key={pret.id} hover>
                    <TableCell>
                      {pret.ref_pret ? (
                        <Chip
                          label={pret.ref_pret}
                          size="small"
                          sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(pret.date_demande)}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {pret.employee?.nom_complet || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {pret.employee?.matricule}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">{formatMoney(pret.montant_pret)}</TableCell>
                    <TableCell align="right">{formatMoney(pret.prelevement_mensuel)}</TableCell>
                    <TableCell align="center">{pret.duree_mois} mois</TableCell>
                    <TableCell>
                      <Tooltip title="Cliquez pour voir l'historique">
                        <Box
                          sx={{
                            minWidth: 120,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#f5f5f5', borderRadius: 1 },
                            p: 0.5,
                          }}
                          onClick={() => handleProgressClick(pret)}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption">
                              {formatMoney(pret.montant_rembourse)}
                            </Typography>
                            <Typography variant="caption" fontWeight="bold">
                              {pret.pourcentage_rembourse.toFixed(0)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={pret.pourcentage_rembourse}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              bgcolor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: pret.pourcentage_rembourse >= 100 ? '#66BB6A' : HR_COLOR,
                              },
                            }}
                          />
                          {pret.mois_restants !== null && pret.mois_restants > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {pret.mois_restants} mois restants
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatutPretLabel(pret.statut)}
                        size="small"
                        color={getStatutPretColor(pret.statut)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {pret.statut === 'EN_ATTENTE' && (
                          <>
                            <Tooltip title="Valider/Refuser">
                              <IconButton
                                size="small"
                                onClick={() => handleValidateClick(pret)}
                                sx={{ color: '#66BB6A' }}
                              >
                                <ValidateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Modifier">
                              <IconButton
                                size="small"
                                onClick={() => handleEditPret(pret)}
                                sx={{ color: HR_COLOR }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {pret.statut === 'VALIDEE' && (
                          <>
                            <Tooltip title="PDF Français">
                              <IconButton
                                size="small"
                                onClick={() => handleGeneratePDF(pret, 'fr')}
                                sx={{ color: '#1976D2' }}
                              >
                                <PdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="PDF Arabe">
                              <IconButton
                                size="small"
                                onClick={() => handleGeneratePDF(pret, 'ar')}
                                sx={{ color: '#9C27B0' }}
                              >
                                <PdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title="Supprimer">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(pret)}
                            sx={{ color: '#EF5350' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Lignes par page"
        />
      </Paper>

      {/* Form Dialog */}
      <PretFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setFormMinimized(false);
        }}
        pret={selectedPret}
        employees={employees}
        onSuccess={handleSuccess}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      {/* Validation Dialog */}
      <PretValidationDialog
        open={validationDialogOpen}
        onClose={() => setValidationDialogOpen(false)}
        pret={pretToValidate}
        onSuccess={handleSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        type="warning"
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer la demande de prêt de ${pretToDelete?.employee?.nom_complet || 'cet employé'} ?`}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        showCancel={true}
      />

      {/* Historique Dialog */}
      <PretHistoriqueDialog
        open={historiqueDialogOpen}
        onClose={() => setHistoriqueDialogOpen(false)}
        pret={pretForHistorique}
        onSuccess={handleSuccess}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default PretsManagement;
